import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Slider } from "../components/ui/slider";
import { Input } from "../components/ui/input";
import { CollectionEditor } from "../components/admin/CollectionEditor";
import { AIHelper } from "../components/admin/AIHelper";
import { SCHEMAS } from "../components/admin/schemas";
import { VoicePanel } from "../components/admin/VoicePanel";

const SettingsPanel = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)); }, []);
  if (!s) return null;
  const save = async () => { await api.put("/admin/settings", { threshold: s.threshold, min_attempts: s.min_attempts }); toast.success("Mastery settings saved"); };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="scroll-card p-6 space-y-5" data-testid="mastery-settings">
        <h3 className="font-bold">Mastery rule</h3>
        <div>
          <div className="text-sm text-stone-600 mb-2">Accuracy threshold: <b data-testid="threshold-value">{Math.round(s.threshold * 100)}%</b></div>
          <Slider data-testid="threshold-slider" min={50} max={100} step={5} value={[Math.round(s.threshold * 100)]} onValueChange={([v]) => setS({ ...s, threshold: v / 100 })} />
        </div>
        <div>
          <div className="text-sm text-stone-600 mb-2">Minimum attempts</div>
          <Input data-testid="min-attempts-input" type="number" min={1} value={s.min_attempts} onChange={(e) => setS({ ...s, min_attempts: Number(e.target.value) })} className="w-32" />
        </div>
        <button data-testid="save-settings-btn" onClick={save} className="h-10 px-5 rounded-full bg-[var(--vermilion)] text-white text-sm font-semibold">Save</button>
      </div>
      <VoicePanel />
    </div>
  );
};

const Analytics = () => {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setA(data)); }, []);
  if (!a) return null;
  const items = [
    ["Registrations", a.registrations], ["Child profiles", a.child_profiles], ["Lessons started", a.lessons_started], ["Lessons completed", a.lessons_completed],
    ["Avg lesson (min)", a.avg_lesson_minutes], ["Activities completed", a.activities_completed], ["Accuracy", a.accuracy == null ? "—" : `${Math.round(a.accuracy * 100)}%`],
    ["Replays", a.replay_count], ["Characters practiced", a.characters_practiced], ["Zhuyin practiced", a.zhuyin_practiced],
    ["Weekly active children", a.weekly_active_children], ["Dashboard visits", a.dashboard_visits],
  ];
  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map(([l, v]) => <div key={l} className="scroll-card p-4"><div className="text-xs uppercase text-stone-500">{l}</div><div className="text-3xl font-bold text-[var(--tea)] mt-1">{v}</div></div>)}
      </div>
      <div className="scroll-card p-6">
        <h3 className="font-bold mb-3">Drop-off point (unfinished sessions by last step)</h3>
        <div className="flex flex-wrap gap-3">{Object.entries(a.drop_off).map(([k, v]) => <span key={k} className="px-3 py-1 rounded-full bg-amber-50 border text-sm">{k}: <b>{v}</b></span>)}{!Object.keys(a.drop_off).length && <span className="text-sm text-stone-400">No data yet</span>}</div>
      </div>
    </div>
  );
};

const Messages = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/contact").then(({ data }) => setRows(data)); }, []);
  return (
    <div className="space-y-3" data-testid="admin-messages">
      {rows.map((r) => (
        <div key={r.id} className="scroll-card !rounded-2xl p-5">
          <div className="flex gap-3 text-sm"><b>{r.name}</b><a className="text-[var(--vermilion)]" href={`mailto:${r.email}`}>{r.email}</a><span className="ml-auto text-stone-400">{r.created_at.slice(0, 16).replace("T", " ")}</span></div>
          <p className="mt-2 whitespace-pre-wrap font-serif-tc">{r.message}</p>
        </div>
      ))}
      {!rows.length && <div className="text-stone-400">No messages yet.</div>}
    </div>
  );
};

const Audit = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/audit").then(({ data }) => setRows(data)); }, []);
  return (
    <div className="scroll-card p-4 max-h-[65vh] overflow-auto text-sm" data-testid="admin-audit">
      {rows.map((r) => <div key={r.id} className="py-2 border-b last:border-0 flex gap-4"><span className="text-stone-400 w-44 shrink-0">{r.at.slice(0, 19).replace("T", " ")}</span><span className="font-semibold w-20">{r.action}</span><span>{r.collection} · {r.item_id}</span><span className="text-stone-500 ml-auto">{r.email}</span></div>)}
      {!rows.length && <div className="text-stone-400 p-4">No admin changes yet.</div>}
    </div>
  );
};

export default function Admin() {
  const nav = useNavigate();
  const [stories, setStories] = useState([]);
  const loadStories = () => api.get("/admin/c/stories").then(({ data }) => setStories(data));
  useEffect(() => { loadStories(); }, []);
  return (
    <div className="min-h-screen paper-bg font-en" data-testid="admin-page">
      <header className="bg-[var(--ink)] text-white">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-4">
          <button data-testid="admin-back-btn" onClick={() => nav("/parent")} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"><ArrowLeft size={18} /></button>
          <div className="font-brush text-3xl text-amber-300">小書童</div>
          <div className="font-semibold">Content Studio</div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-6">
        <Tabs defaultValue="stories">
          <TabsList className="bg-amber-100 flex-wrap h-auto">
            {Object.entries(SCHEMAS).map(([k, s]) => <TabsTrigger key={k} value={k} data-testid={`admin-tab-${k}`}>{s.label}</TabsTrigger>)}
            <TabsTrigger value="ai" data-testid="admin-tab-ai">AI Assistant</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="admin-tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit log</TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-tab-messages">Messages</TabsTrigger>
          </TabsList>
          {Object.keys(SCHEMAS).map((k) => <TabsContent key={k} value={k} className="mt-6"><CollectionEditor name={k} stories={stories} onChanged={k === "stories" ? loadStories : undefined} /></TabsContent>)}
          <TabsContent value="ai" className="mt-6">{stories.length > 0 && <AIHelper stories={stories} />}</TabsContent>
          <TabsContent value="settings" className="mt-6"><SettingsPanel /></TabsContent>
          <TabsContent value="analytics" className="mt-6"><Analytics /></TabsContent>
          <TabsContent value="audit" className="mt-6"><Audit /></TabsContent>
          <TabsContent value="messages" className="mt-6"><Messages /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
